import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Database, Plus, Trash2, ChevronDown, ChevronRight, Table2,
  Key, Link2, Hash, Type, Calendar, ToggleLeft, FileText,
  Server, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dqp_token')}` } });

const DB_ICONS = { postgresql: '🐘', bigquery: '📊', snowflake: '❄️', mongodb: '🍃', mysql: '🐬', alloydb: '⚡' };
const DB_COLORS = { postgresql: '#336791', bigquery: '#4285F4', snowflake: '#29B5E8', mongodb: '#47A248', mysql: '#4479A1', alloydb: '#4285F4' };

const typeIcon = (col) => {
  if (col.is_pk) return <Key size={12} className="text-amber-500" />;
  if (col.is_fk) return <Link2 size={12} className="text-blue-500" />;
  if (col.type?.includes('int') || col.type?.includes('decimal')) return <Hash size={12} className="text-emerald-500" />;
  if (col.type?.includes('date') || col.type?.includes('time')) return <Calendar size={12} className="text-purple-500" />;
  if (col.type?.includes('bool')) return <ToggleLeft size={12} className="text-sky-500" />;
  if (col.type?.includes('text')) return <FileText size={12} className="text-orange-500" />;
  return <Type size={12} className="text-gray-400" />;
};

function SchemaTree({ schema, profiles }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (name) => setExpanded(p => ({ ...p, [name]: !p[name] }));
  if (!schema?.tables) return null;
  return (
    <div className="space-y-1">
      {schema.tables.map(table => (
        <div key={table.name}>
          <button
            data-testid={`schema-table-${table.name}`}
            onClick={() => toggle(table.name)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#f5f6fb] text-left transition-colors group"
          >
            {expanded[table.name] ? <ChevronDown size={14} className="text-[#9ca3af]" /> : <ChevronRight size={14} className="text-[#9ca3af]" />}
            <Table2 size={14} className="text-[#5b63ea]" />
            <span className="text-sm font-medium text-[#1e1e2e]">{table.name}</span>
            <span className="text-xs text-[#9ca3af] ml-auto">{table.row_count} rows · {table.columns.length} cols</span>
          </button>
          {expanded[table.name] && (
            <div className="ml-8 border-l border-[#e6e8f2] pl-3 py-1 space-y-0.5">
              {table.columns.map(col => {
                const prof = profiles?.[table.name]?.[col.name];
                return (
                  <div key={col.name} className="flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-[#f5f6fb] group">
                    {typeIcon(col)}
                    <span className="text-[#1e1e2e] font-medium">{col.name}</span>
                    <span className="text-[#9ca3af]">{col.type}</span>
                    {col.nullable && <span className="text-[#d4d8e6]">nullable</span>}
                    {col.is_pk && <span className="bg-amber-100 text-amber-700 px-1 py-0.5 rounded text-[10px] font-medium">PK</span>}
                    {col.is_fk && <span className="bg-blue-100 text-blue-700 px-1 py-0.5 rounded text-[10px] font-medium">FK→{col.fk_table}</span>}
                    {prof && (
                      <span className="ml-auto text-[#9ca3af] hidden group-hover:inline">
                        {prof.distinct_count} distinct · {prof.null_pct}% null
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function DataSources() {
  const [sources, setSources] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: '',
    type: 'postgresql',
    host: 'postgres',
    port: '5432',
    database: 'healthcare_db',
    user: '',
    password: '',
  });
  const [connecting, setConnecting] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);

  const loadSources = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/datasources`, authH());
      setSources(data);
    } catch {}
  }, []);

  useEffect(() => { loadSources(); }, [loadSources]);

  const selectSource = async (id) => {
    setSelected(id);
    try {
      const { data } = await axios.get(`${API}/datasources/${id}`, authH());
      setSelectedDetail(data);
    } catch { toast.error('Failed to load source details'); }
  };

  const handleConnect = async () => {
    if (!form.name || !form.type) { toast.error('Please fill in all fields'); return; }
    setConnecting(true);
    try {
      await axios.post(`${API}/datasources/connect`, {
        name: form.name,
        type: form.type,
        host: form.host,
        database: form.database,
        port: parseInt(form.port, 10) || 5432,
        user: form.user?.trim() || '',
        password: form.password || '',
      }, authH());
      toast.success('Data source connected successfully!');
      setShowAdd(false);
      setForm({
        name: '',
        type: 'postgresql',
        host: 'postgres',
        port: '5432',
        database: 'healthcare_db',
        user: '',
        password: '',
      });
      setWizardStep(0);
      loadSources();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    }
    setConnecting(false);
  };

  const deleteSource = async (id) => {
    try {
      await axios.delete(`${API}/datasources/${id}`, authH());
      toast.success('Source removed');
      if (selected === id) { setSelected(null); setSelectedDetail(null); }
      loadSources();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 data-testid="data-sources-title" className="text-2xl font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>Data Sources</h1>
            <p className="text-[#6b7280] text-sm mt-1">Manage your database connections</p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="add-source-btn" onClick={() => { setShowAdd(true); setWizardStep(0); }} className="bg-[#5b63ea] hover:bg-[#4d56e0] text-white rounded-lg">
              <Plus size={16} className="mr-2" /> Add Connection
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Source List */}
          <div className="space-y-3">
            {sources.length === 0 ? (
              <div className="bg-white rounded-xl border border-[#e6e8f2] p-8 text-center">
                <Database size={40} className="mx-auto text-[#d4d8e6] mb-3" />
                <p className="text-[#6b7280] text-sm">No data sources connected yet</p>
                <Button data-testid="empty-add-source-btn" onClick={() => setShowAdd(true)} variant="outline" className="mt-4 text-[#5b63ea] border-[#5b63ea]/30">
                  Connect your first source
                </Button>
              </div>
            ) : sources.map(src => (
              <motion.div
                key={src.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`source-card-${src.id}`}
                onClick={() => selectSource(src.id)}
                className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md group ${
                  selected === src.id ? 'border-[#5b63ea] shadow-md' : 'border-[#e6e8f2]'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-lg" style={{ backgroundColor: `${DB_COLORS[src.type] || '#666'}15` }}>
                      {DB_ICONS[src.type] || '📁'}
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[#1e1e2e]">{src.name}</h3>
                      <p className="text-xs text-[#9ca3af]">{src.type} · {src.table_count} tables</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">Connected</span>
                    <button onClick={(e) => { e.stopPropagation(); deleteSource(src.id); }} className="opacity-0 group-hover:opacity-100 text-[#9ca3af] hover:text-red-500 transition-all">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-[#9ca3af]">
                  <span>{src.total_rows?.toLocaleString()} rows</span>
                  <span>{src.discovery_status === 'completed' ? '✓ Discovered' : 'Pending discovery'}</span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Schema Explorer */}
          <div className="lg:col-span-2">
            {selectedDetail ? (
              <div className="bg-white rounded-xl border border-[#e6e8f2] overflow-hidden">
                <div className="px-5 py-4 border-b border-[#e6e8f2] flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>Schema Explorer</h2>
                    <p className="text-xs text-[#9ca3af]">{selectedDetail.name} · {selectedDetail.schema?.tables?.length} tables</p>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/app/data-sources/${selected}/profile`}>
                      <Button variant="outline" size="sm" className="text-xs border-[#e6e8f2] text-[#6b7280]">View Profile</Button>
                    </Link>
                  </div>
                </div>
                <div className="scrollbar-light p-4 max-h-[600px] overflow-y-auto min-h-0">
                  <SchemaTree schema={selectedDetail.schema} profiles={selectedDetail.profiles} />
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-[#e6e8f2] p-12 text-center">
                <Database size={48} className="mx-auto text-[#d4d8e6] mb-3" />
                <p className="text-[#6b7280]">Select a data source to explore its schema</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Source Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-white border-[#e6e8f2] max-w-lg" data-testid="add-source-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>
              {wizardStep === 0 ? 'Select Source Type' : wizardStep === 1 ? 'Connection Details' : 'Confirm Connection'}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6b7280]">
              {wizardStep === 0 ? 'Choose a database type to connect.' : wizardStep === 1 ? 'Enter your database connection details.' : 'Review and confirm your connection settings.'}
            </DialogDescription>
          </DialogHeader>

          {wizardStep === 0 && (
            <div className="grid grid-cols-2 gap-3 py-4">
              {Object.entries(DB_ICONS).map(([type, icon]) => (
                <button
                  key={type}
                  data-testid={`source-type-${type}`}
                  onClick={() => { setForm(f => ({ ...f, type })); setWizardStep(1); }}
                  className={`p-4 rounded-xl border text-left transition-all hover:shadow-md ${
                    form.type === type ? 'border-[#5b63ea] bg-[#5b63ea]/5' : 'border-[#e6e8f2] hover:border-[#5b63ea]/30'
                  }`}
                >
                  <span className="text-2xl">{icon}</span>
                  <p className="text-sm font-medium text-[#1e1e2e] mt-2 capitalize">{type}</p>
                  <p className="text-[10px] text-[#9ca3af] mt-0.5">via MCP Toolbox</p>
                </button>
              ))}
            </div>
          )}

          {wizardStep === 1 && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[#1e1e2e] text-sm mb-1.5 block">Connection Name</Label>
                <Input data-testid="source-name-input" placeholder="e.g. Production Healthcare DB" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#f5f6fb] border-[#e6e8f2] text-[#1e1e2e]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#1e1e2e] text-sm mb-1.5 block">Host</Label>
                  <Input data-testid="source-host-input" value={form.host} onChange={e => setForm(f => ({ ...f, host: e.target.value }))} className="bg-[#f5f6fb] border-[#e6e8f2] text-[#1e1e2e]" />
                </div>
                <div>
                  <Label className="text-[#1e1e2e] text-sm mb-1.5 block">Port</Label>
                  <Input data-testid="source-port-input" value={form.port} onChange={e => setForm(f => ({ ...f, port: e.target.value }))} className="bg-[#f5f6fb] border-[#e6e8f2] text-[#1e1e2e]" />
                </div>
              </div>
              <div>
                <Label className="text-[#1e1e2e] text-sm mb-1.5 block">Database</Label>
                <Input data-testid="source-database-input" value={form.database} onChange={e => setForm(f => ({ ...f, database: e.target.value }))} className="bg-[#f5f6fb] border-[#e6e8f2] text-[#1e1e2e]" />
              </div>
              {form.type?.toLowerCase() === 'postgresql' && (
                <>
                  <div>
                    <Label className="text-[#1e1e2e] text-sm mb-1.5 block">Username</Label>
                    <Input
                      data-testid="source-user-input"
                      autoComplete="username"
                      placeholder="Database user"
                      value={form.user}
                      onChange={(e) => setForm((f) => ({ ...f, user: e.target.value }))}
                      className="bg-[#f5f6fb] border-[#e6e8f2] text-[#1e1e2e]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#1e1e2e] text-sm mb-1.5 block">Password</Label>
                    <Input
                      data-testid="source-password-input"
                      type="password"
                      autoComplete="current-password"
                      placeholder="Database password"
                      value={form.password}
                      onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                      className="bg-[#f5f6fb] border-[#e6e8f2] text-[#1e1e2e]"
                    />
                  </div>
                  <p className="text-[11px] text-[#9ca3af]">
                    Use your database login for remote servers. Leave blank only if this deployment uses matching defaults from its environment.
                  </p>
                </>
              )}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setWizardStep(0)} className="border-[#e6e8f2] text-[#6b7280]">Back</Button>
                <Button data-testid="source-next-btn" onClick={() => setWizardStep(2)} className="flex-1 bg-[#5b63ea] hover:bg-[#4d56e0] text-white">Next</Button>
              </div>
            </div>
          )}

          {wizardStep === 2 && (
            <div className="py-4 space-y-4">
              <div className="bg-[#f5f6fb] rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#9ca3af]">Type</span><span className="text-[#1e1e2e] font-medium capitalize">{form.type}</span></div>
                <div className="flex justify-between"><span className="text-[#9ca3af]">Name</span><span className="text-[#1e1e2e] font-medium">{form.name}</span></div>
                <div className="flex justify-between"><span className="text-[#9ca3af]">Host</span><span className="text-[#1e1e2e] font-mono text-xs">{form.host}:{form.port}</span></div>
                <div className="flex justify-between"><span className="text-[#9ca3af]">Database</span><span className="text-[#1e1e2e] font-medium">{form.database}</span></div>
                {form.type?.toLowerCase() === 'postgresql' && (
                  <>
                    <div className="flex justify-between"><span className="text-[#9ca3af]">Username</span><span className="text-[#1e1e2e] font-medium font-mono text-xs">{form.user || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-[#9ca3af]">Password</span><span className="text-[#1e1e2e] font-medium">{form.password ? '••••••••' : '—'}</span></div>
                  </>
                )}
              </div>
              <p className="text-xs text-[#9ca3af] text-center">Connection will be established via MCP Toolbox</p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setWizardStep(1)} className="border-[#e6e8f2] text-[#6b7280]">Back</Button>
                <Button data-testid="source-connect-btn" onClick={handleConnect} disabled={connecting} className="flex-1 bg-[#5b63ea] hover:bg-[#4d56e0] text-white">
                  {connecting ? <><RefreshCw size={14} className="mr-2 animate-spin" /> Connecting...</> : 'Connect'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
